import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  get,
  type PublicUser,
  type RepoList as RepoPage,
} from "../../services/api";
import { useAuth } from "../../hooks/AuthContext";
import { RepoList } from "../../components/RepoList";
import { Avatar, ErrorBox, Loading, Pagination } from "../../components/Ui";
import { fullDate } from "../../shared/utils/format";
import { useAsync, useTitle } from "../../hooks";
import { NotFound } from "./NotFound";
import { useState } from "react";
import {
  socialStatus,
  type SocialStatus,
  type PeoplePage,
} from "../../services/social";
import { FollowButton } from "../../components/FollowButton";
import { PeopleList } from "../../components/PeopleList";
import { CommunityAside } from "../../components/CommunityAside";
import { Icon } from "../../components/Icon";

export function Profile() {
  const { owner = "" } = useParams();
  const { user: viewer } = useAuth();
  const [params, setParams] = useSearchParams();
  const requestedTab = params.get("tab") ?? "overview";
  const tab = ["wikis", "followers", "following"].includes(requestedTab)
    ? requestedTab
    : "overview";
  const [updatedStatus, setUpdatedStatus] = useState<{
    owner: string;
    status: SocialStatus;
  } | null>(null);
  const social = useAsync(() => socialStatus(owner), [owner]);
  const connections = useAsync(
    () =>
      ["following", "followers"].includes(tab)
        ? get<PeoplePage>(
            `/users/${encodeURIComponent(owner)}/${tab}?page=${Math.min(10000, Math.max(1, Number(params.get("page")) || 1))}&perPage=20`,
          )
        : Promise.resolve(null),
    [owner, tab, params.get("page")],
  );
  const page = Math.min(
    10000,
    Math.max(1, Math.floor(Number(params.get("page")) || 1)),
  );
  const profile = useAsync(
    () =>
      get<PublicUser & { publicRepositories: number }>(
        `/users/${encodeURIComponent(owner)}`,
      ),
    [owner],
  );
  const repos = useAsync(
    () =>
      ["followers", "following"].includes(tab)
        ? Promise.resolve(null)
        : get<RepoPage>(
            `/users/${encodeURIComponent(owner)}/repositories?perPage=${tab === "overview" ? 6 : 20}&page=${tab === "overview" ? 1 : page}`,
          ),
    [owner, viewer?.id, tab, page],
  );
  useTitle(owner);
  if (profile.error?.status === 404) return <NotFound what="user" />;
  if (profile.error)
    return (
      <div className="container">
        <ErrorBox error={profile.error} retry={profile.reload} />
      </div>
    );
  if (!profile.data)
    return (
      <div className="container">
        <Loading />
      </div>
    );
  const user = profile.data;
  const counts =
    (updatedStatus?.owner === owner ? updatedStatus.status : null) ??
    social.data;
  const self = viewer?.username.toLowerCase() === user.username.toLowerCase();
  return (
    <div className="community-layout profile-page">
      <div className="profile community-stream">
        <div className="profile-cover" aria-hidden="true" />
        <section className="profile-identity stack">
          <div className="profile-actions">
            {self ? (
              <Link className="btn" to="/settings/profile">
                Edit profile
              </Link>
            ) : (
              counts && (
                <FollowButton
                  username={owner}
                  status={counts}
                  onChange={(status) => setUpdatedStatus({ owner, status })}
                />
              )
            )}
          </div>
          <Avatar name={user.displayName || user.username} large />
          <div>
            <h1>{user.displayName || user.username}</h1>
            <p className="muted">@{user.username}</p>
          </div>
          {user.bio && <p>{user.bio}</p>}
          <p className="muted row">
            <Icon name="history" />
            Joined {fullDate(user.createdAt)}
          </p>
          <p>
            <Icon name="book" /> {user.publicRepositories} public{" "}
            {user.publicRepositories === 1 ? "wiki" : "wikis"}
          </p>
          {social.error && (
            <ErrorBox error={social.error} retry={social.reload} />
          )}
          {counts && (
            <div className="profile-connections">
              <Link to={`/user/${owner}?tab=following`}>
                <strong>{counts.following}</strong> Following
              </Link>
              <Link to={`/user/${owner}?tab=followers`}>
                <strong>{counts.followers}</strong> Followers
              </Link>
            </div>
          )}
        </section>
        <section aria-label="Profile content">
          <nav className="tabs" aria-label="Profile sections">
            <Link
              to={`/user/${owner}`}
              aria-current={tab === "overview" ? "page" : undefined}
            >
              Overview
            </Link>
            <Link
              to={`/user/${owner}?tab=wikis`}
              aria-current={tab === "wikis" ? "page" : undefined}
            >
              Wikis
            </Link>
            <Link
              to={`/user/${owner}?tab=followers`}
              aria-current={tab === "followers" ? "page" : undefined}
            >
              Followers
            </Link>
            <Link
              to={`/user/${owner}?tab=following`}
              aria-current={tab === "following" ? "page" : undefined}
            >
              Following
            </Link>
          </nav>
          {["followers", "following"].includes(tab) ? (
            connections.loading ? (
              <Loading />
            ) : connections.error ? (
              <ErrorBox error={connections.error} retry={connections.reload} />
            ) : (
              <>
                <PeopleList people={connections.data?.items ?? []} />
                {connections.data && (
                  <Pagination
                    page={connections.data.page}
                    perPage={20}
                    total={connections.data.total}
                    onPage={(value) => setParams({ tab, page: String(value) })}
                  />
                )}
              </>
            )
          ) : (
            <>
              <div className="section-title">
                <h2 id="profile-wikis">
                  {tab === "overview" ? "Recently updated wikis" : "Wikis"}
                </h2>
                <p className="hint">
                  Public wikis and private wikis you have permission to read.
                </p>
              </div>
              {repos.loading && !repos.data ? (
                <Loading />
              ) : repos.error ? (
                <ErrorBox error={repos.error} retry={repos.reload} />
              ) : (
                <>
                  <div className="card">
                    <RepoList
                      repos={repos.data?.items ?? []}
                      empty={`${user.username} has no wikis you can see`}
                    />
                  </div>
                  {tab === "wikis" && repos.data && (
                    <Pagination
                      page={repos.data.page}
                      perPage={repos.data.perPage}
                      total={repos.data.total}
                      onPage={(value) =>
                        setParams({ tab: "wikis", page: String(value) })
                      }
                    />
                  )}
                  {tab === "overview" && (repos.data?.total ?? 0) > 6 && (
                    <Link className="btn" to={`/user/${owner}?tab=wikis`}>
                      View all visible wikis
                    </Link>
                  )}
                </>
              )}
            </>
          )}
        </section>
      </div>
      <CommunityAside />
    </div>
  );
}
